import { isPlatformBrowser, ViewportScroller } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  Scroll,
} from '@angular/router';

/**
 * Restores the target page's last scroll position on a breadcrumb-trail
 * click.
 *
 * `withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })`
 * (customer-providers.ts) restores scroll only on POPSTATE (Back/Forward); an
 * imperative `routerLink` click — which is what every breadcrumb crumb does —
 * always lands at the top. `BreadcrumbTrailComponent`'s non-current crumbs
 * pass `[state]="{ restoreScroll: true }"` on their `routerLink`; this
 * service records where the user was on every route (`NavigationStart`,
 * before the URL changes) and, on the router's `Scroll` event for a
 * `restoreScroll` navigation with no popstate position and no fragment,
 * scrolls back there (spec §13.3, 2026-09-11). Nav-bar, drawer, footer and
 * in-content links carry no such state, so they are unaffected and still
 * land at the top.
 *
 * The `restoreScroll` flag lives on `router.currentNavigation()`, which is
 * only non-null while a navigation is IN FLIGHT — by the time the `Scroll`
 * event fires (after `NavigationEnd`) it is already `null` again. So the flag
 * is read once, at `NavigationStart`, and held until the matching `Scroll`
 * event arrives — and CONSUMED there (reset to `false` in `maybeRestore`,
 * regardless of whether a stored position actually existed to restore).
 * Consuming it matters because a `Scroll` event isn't only produced by a real
 * `NavigationEnd`: a same-URL navigation (e.g. clicking an ordinary link back
 * to the page a breadcrumb restore just landed on) fires `NavigationSkipped`
 * instead of `NavigationStart` — so the flag never gets a fresh
 * `NavigationStart` to overwrite it — and `RouterScroller` schedules a
 * `Scroll` event for that skip exactly as it does for `NavigationEnd`. Left
 * armed, that stale flag would make THIS unrelated, no-`restoreScroll` link
 * "restore" against an old stored position instead of landing at the top,
 * which is exactly the behavior spec §13.3 says nav-bar/footer/in-content
 * links must NOT have. The flag is also cleared on `NavigationCancel` /
 * `NavigationError`, since a cancelled or errored navigation never reaches
 * `NavigationEnd` and so produces no `Scroll` event to consume it otherwise.
 *
 * Deferred via `queueMicrotask`: the router's own `RouterScroller` (the
 * internal service `withInMemoryScrolling` wires up) reacts to that SAME
 * null-position/null-anchor `Scroll` event by calling
 * `scrollToPosition([0, 0])` SYNCHRONOUSLY, inside its own subscription to
 * the router's INTERNAL event feed (`NavigationTransitions.events` — a
 * different Subject from the public `router.events` this service reads, so
 * subscriber order between the two is not something this service can rely on
 * or control). Queuing the restore for the next microtask guarantees it runs
 * AFTER that synchronous call completes, regardless of subscriber order, and
 * so wins.
 *
 * Browser-only: `ViewportScroller` position reads/writes are meaningless
 * during SSR/prerender, so the whole subscription is skipped on the server.
 */
@Injectable({ providedIn: 'root' })
export class ScrollMemoryService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly viewportScroller = inject(ViewportScroller);

  private readonly positions = new Map<string, [number, number]>();
  private pendingRestore = false;

  constructor() {
    if (!isPlatformBrowser(this.platformId)) return;

    this.router.events.pipe(takeUntilDestroyed()).subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.recordPosition();
        this.pendingRestore =
          this.router.currentNavigation()?.extras.state?.['restoreScroll'] ===
          true;
        return;
      }

      if (
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        // No NavigationEnd (and so no Scroll event) will follow this
        // navigation to consume the flag via maybeRestore — clear it here so
        // it can't leak forward onto whatever Scroll event comes next.
        this.pendingRestore = false;
        return;
      }

      if (event instanceof Scroll) {
        this.maybeRestore(event);
      }
    });
  }

  private recordPosition(): void {
    this.positions.set(
      this.router.url,
      this.viewportScroller.getScrollPosition(),
    );
  }

  private maybeRestore(event: Scroll): void {
    // Consume-and-clear, unconditionally: this Scroll event concludes
    // whichever navigation last set the flag, whether that navigation ended
    // in a real NavigationEnd or was skipped as a same-URL no-op
    // (NavigationSkipped) — RouterScroller schedules a Scroll event for both.
    // Clearing only inside the `shouldRestore` branch below would leave the
    // flag armed on every OTHER path through this method (no stored
    // position, a popstate/anchor scroll), each of which is exactly the kind
    // of unrelated later Scroll event the flag must not leak onto.
    const shouldRestore = this.pendingRestore;
    this.pendingRestore = false;

    if (event.position !== null || event.anchor !== null) return;
    if (!shouldRestore) return;

    const url =
      event.routerEvent instanceof NavigationEnd
        ? event.routerEvent.urlAfterRedirects
        : event.routerEvent.url;
    const stored = this.positions.get(url);
    if (!stored) return;

    queueMicrotask(() => {
      this.viewportScroller.scrollToPosition(stored);
    });
  }
}
